import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, HostListener, inject, Input, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@rms/auth';
import { NavigationItem, NavigationService } from '@rms/core/navigation';
import { UserInitialsPipe } from '@rms/sdk/pipes';
import { UserStoreService } from '@rms/user';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { LAYOUT } from '../../layout.constants';
import { LayoutService } from '../../services/layout.service';
import { ScrollbarComponent } from '../scrollbar/scrollbar.component';
import { SidenavItemComponent } from './sidenav-item/sidenav-item.component';

@Component({
  selector: 'rms-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  imports: [
    MatButtonModule,
    MatIconModule,
    MatRippleModule,
    MatTooltipModule,
    MatMenuModule,
    ScrollbarComponent,
    SidenavItemComponent,
    AsyncPipe,
    TranslateModule,
    UserInitialsPipe,
    RouterModule,
  ],
})
export class SidenavComponent {
  @Input() public collapsed = false;
  @ViewChild('userMenu') public userMenu?: MatMenu;
  @HostListener('window:resize')
  public onResize(): void {
    this.userMenu?.closed.emit();
  }

  public readonly userVisible = LAYOUT.sidenavUserVisible;

  public readonly collapsedOpen$: Observable<boolean>;
  public readonly isDesktop$: Observable<boolean>;
  public readonly logoUrl$: Observable<string>;
  public readonly userName$: Observable<string>;
  public readonly items$: Observable<NavigationItem[]>;

  protected readonly destroyRef = inject(DestroyRef);

  private readonly navigationService = inject(NavigationService);
  private readonly layoutService = inject(LayoutService);
  private readonly userStore = inject(UserStoreService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  constructor() {
    this.collapsedOpen$ = this.layoutService.sidenavCollapsedOpen$;
    this.isDesktop$ = this.layoutService.isDesktop$;
    this.items$ = this.navigationService.items$;
    this.userName$ = this.userStore.user$.pipe(map((user) => user.full_name));
    this.logoUrl$ = this.layoutService.sidenavCollapsed$.pipe(
      map((collapsed) => (collapsed ? LAYOUT.sidenavCollapsedLogoUrl : LAYOUT.sidenavLogoUrl))
    );
  }

  public collapseOpenSidenav(): void {
    this.layoutService.collapseOpenSidenav();
  }

  public collapseCloseSidenav(): void {
    this.layoutService.collapseCloseSidenav();
  }

  public toggleCollapse(): void {
    if (this.collapsed) {
      this.layoutService.expandSidenav();
    } else {
      this.layoutService.collapseSidenav();
    }
  }

  public trackByRoute(_: number, item: NavigationItem): string {
    return item.type === 'link' ? item.route : item.label;
  }

  public logout(): void {
    this.authService
      .logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.router.navigate(['/login']));
  }
}
